import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { SearchState } from 'src/app/interfaces/search.interface';
import { EmptyResponse, PatientDistributionFilter, RelationGeography } from 'src/app/swagger-api';
import { DefaultService } from 'src/app/swagger-api/api/default.service';
import { ConnectionRequestAction } from 'src/app/swagger-api/model/connectionRequestAction';
import { assertNonNullish } from 'src/app/utils';
import { SearchCategories } from '../../search/search.enum';
import { mapDoctorLink, mapRequestCount } from '../relation-request';
import {
  DoctorLink,
  DoctorLinkRequests,
  RelationRequests,
  RelationRequestsCount,
  SuggestedUser,
} from '../relation-request.interface';
import { UserRelationRequestService } from './relation-request.service';

@Injectable()
export class DoctorLinkRequestService extends UserRelationRequestService<DoctorLink> {
  private apiService = inject(DefaultService);

  protected fetchTotalCountRequests(): Observable<RelationRequestsCount> {
    return this.apiService.restMedicalDoctorLinksCounters().pipe(map(mapRequestCount));
  }

  protected updateSearchState(state: SearchState): SearchState {
    const { params } = state;

    return {
      ...state,
      params: { queryText: params?.queryText ?? '', itemKind: SearchCategories.DOCTORS },
    };
  }

  protected fetchAcceptedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<DoctorLink>> {
    return this.apiService.restMedicalDoctorLinksIndexLinked(offset, limit, sorting).pipe(
      map((requests: DoctorLinkRequests) => ({
        items: requests.items.map(mapDoctorLink),
        totalCount: requests.total_count,
      })),
    );
  }

  protected fetchReceivedRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<DoctorLink>> {
    return this.apiService.restMedicalDoctorLinksIndexReceived(offset, limit, sorting).pipe(
      map((requests: DoctorLinkRequests) => ({
        items: requests.items.map(mapDoctorLink),
        totalCount: requests.total_count,
      })),
    );
  }

  protected fetchSentRequests(
    offset?: number,
    limit?: number,
    sorting?: string,
  ): Observable<RelationRequests<DoctorLink>> {
    return this.apiService.restMedicalDoctorLinksIndexSent(offset, limit, sorting).pipe(
      map((requests: DoctorLinkRequests) => ({
        items: requests.items.map(mapDoctorLink),
        totalCount: requests.total_count,
      })),
    );
  }

  protected fetchRegularUserRelation(_: string): Observable<RelationRequests<DoctorLink>> {
    throw new Error('Method not implemented.');
  }

  protected fetchRelationGeography(
    _userId: string,
    _filter_by?: PatientDistributionFilter,
    _filter_value?: string,
  ): Observable<RelationGeography> {
    throw new Error('Method not implemented.');
  }

  protected accept({ entityId }: DoctorLink): Observable<EmptyResponse> {
    return this.apiService.restMedicalDoctorLinkPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
      entityId,
    ) as Observable<EmptyResponse>;
  }

  protected reject({ entityId }: DoctorLink): Observable<EmptyResponse> {
    return this.apiService.restMedicalDoctorLinkPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Rejected },
      entityId,
    ) as Observable<EmptyResponse>;
  }

  protected remove({ entityId }: DoctorLink): Observable<EmptyResponse> {
    return this.apiService.restMedicalDoctorLinkDelete(entityId) as Observable<EmptyResponse>;
  }

  protected create({ user }: SuggestedUser): Observable<EmptyResponse> {
    return this.apiService.restMedicalDoctorLinksPost({ doctorUserId: user.user_id }) as Observable<EmptyResponse>;
  }

  protected removeByUser(user: SuggestedUser): Observable<EmptyResponse> {
    assertNonNullish(user.doctorLinkStatus?.entity_id);
    return this.apiService.restMedicalDoctorLinkDelete(user.doctorLinkStatus.entity_id) as Observable<EmptyResponse>;
  }

  protected override acceptByUser(user: SuggestedUser): Observable<EmptyResponse> {
    assertNonNullish(user.doctorLinkStatus?.entity_id);
    return this.apiService.restMedicalDoctorLinkPut(
      { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
      user.doctorLinkStatus.entity_id,
    ) as Observable<EmptyResponse>;
  }
}
